import { getUsoMutuoRowKind, isUsoMutuoPosteRow, USO_MUTUO_ROW_KIND } from './nsmpRowTypes';
import { toSucataTimestamp } from './usoMutuoPeriodo';

export const USO_MUTUO_STATUS_CODES = {
  AGUARDANDO: '1',
  EM_ANDAMENTO: '2',
  FINALIZADO: '3',
};

export const USO_MUTUO_TIPO_CHIPS = {
  POSTE: 'poste',
  ANALISE_RISCO: 'analiseRisco',
  ENTREGA_SUCATA: 'entregaSucata',
  PESAGEM_SUCATA: 'pesagemSucata',
};

export const EMPTY_USO_MUTUO_QUICK_FILTER = {
  search: '',
  tipoChip: null,
  statusChip: null,
  situacaoChip: null,
};

export const getStatusDigitacaoCode = (row) => {
  if (row.digitadoEmValue) return USO_MUTUO_STATUS_CODES.FINALIZADO;
  if (row.subNsmpStatus && row.subNsmpStatus.total !== row.subNsmpStatus.aguardandoValidacao) {
    return USO_MUTUO_STATUS_CODES.EM_ANDAMENTO;
  }
  return USO_MUTUO_STATUS_CODES.AGUARDANDO;
};

const KIND_ORDER = {
  [USO_MUTUO_ROW_KIND.ENTREGA_SUCATA]: 0,
  [USO_MUTUO_ROW_KIND.PESAGEM_SUCATA]: 1,
  [USO_MUTUO_ROW_KIND.ANALISE]: 2,
  [USO_MUTUO_ROW_KIND.FOTO_REGISTRO]: 3,
  [USO_MUTUO_ROW_KIND.POSTE]: 4,
};

const parseDisplayDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const parsed = toSucataTimestamp(value);
  if (parsed != null) return parsed;
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])).getTime();
};

const getUsoMutuoSortDate = (row, kind) => {
  if (kind === USO_MUTUO_ROW_KIND.ENTREGA_SUCATA || kind === USO_MUTUO_ROW_KIND.PESAGEM_SUCATA) {
    return toSucataTimestamp(row.sucata?.data);
  }
  if (kind === USO_MUTUO_ROW_KIND.ANALISE) {
    return toSucataTimestamp(row.criadoEm)
      || toSucataTimestamp(row.dataFotoDrone)
      || parseDisplayDate(row.criadoEmValue);
  }
  return toSucataTimestamp(row.criadoEm) || toSucataTimestamp(row.dataFotoDrone);
};

const compareCodigo = (a, b) => {
  const codeA = Number(a.codigo);
  const codeB = Number(b.codigo);
  if (!Number.isNaN(codeA) && !Number.isNaN(codeB)) return codeA - codeB;
  return String(a.codigo ?? '').localeCompare(String(b.codigo ?? ''));
};

export const sortUsoMutuoRows = (nsmps) => {
  if (!nsmps) return [];
  return [...nsmps].sort((a, b) => {
    const kindA = getUsoMutuoRowKind(a);
    const kindB = getUsoMutuoRowKind(b);
    const groupDiff = (KIND_ORDER[kindA] ?? 4) - (KIND_ORDER[kindB] ?? 4);
    if (groupDiff !== 0) return groupDiff;

    if (kindA === USO_MUTUO_ROW_KIND.POSTE) {
      const codigoDiff = compareCodigo(a, b);
      if (codigoDiff !== 0) return codigoDiff;
    } else {
      const dateA = getUsoMutuoSortDate(a, kindA);
      const dateB = getUsoMutuoSortDate(b, kindB);
      const sortA = dateA == null ? Number.POSITIVE_INFINITY : dateA;
      const sortB = dateB == null ? Number.POSITIVE_INFINITY : dateB;
      if (sortA !== sortB) return sortA - sortB;
    }

    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });
};

const matchesTipo = (row, tipoChip) => {
  if (!tipoChip) return true;

  switch (tipoChip) {
    case USO_MUTUO_TIPO_CHIPS.POSTE:
      return isUsoMutuoPosteRow(row);
    case USO_MUTUO_TIPO_CHIPS.ANALISE_RISCO:
      return row.analiseRisco === true;
    case USO_MUTUO_TIPO_CHIPS.ENTREGA_SUCATA:
      return row.tipoNsmp === 'ENTREGA_SUCATA';
    case USO_MUTUO_TIPO_CHIPS.PESAGEM_SUCATA:
      return row.tipoNsmp === 'PESAGEM_SUCATA';
    default:
      return true;
  }
};

const getSituacaoPosteValue = (row) => row.situacaoPoste || 'Vazio';

export const applyUsoMutuoFilters = (rows, filtroValues, fuseInstance) => {
  if (!rows) return [];
  let filteredRows = rows;

  if (filtroValues.tipoChip) {
    filteredRows = filteredRows.filter((p) => matchesTipo(p, filtroValues.tipoChip));
  }

  if (Array.isArray(filtroValues.situacaoPostes) && filtroValues.situacaoPostes.length > 0) {
    filteredRows = filteredRows.filter((p) =>
      !isUsoMutuoPosteRow(p) || filtroValues.situacaoPostes.includes(getSituacaoPosteValue(p))
    );
  }

  if (Array.isArray(filtroValues.statusDigitacao) && filtroValues.statusDigitacao.length > 0) {
    filteredRows = filteredRows.filter((p) =>
      !isUsoMutuoPosteRow(p) || filtroValues.statusDigitacao.includes(getStatusDigitacaoCode(p))
    );
  }

  if (filtroValues.numeroPoste) {
    const termo = filtroValues.numeroPoste.toLowerCase();
    filteredRows = filteredRows.filter(
      (p) => p.numeroPoste && p.numeroPoste.toLowerCase().includes(termo)
    );
  }

  if (filtroValues.endereco && filtroValues.endereco.trim().length >= 2 && fuseInstance) {
    const fuzzyResults = fuseInstance.search(filtroValues.endereco).map((r) => r.item);
    const ids = new Set(fuzzyResults.map((p) => p.id));
    filteredRows = filteredRows.filter((p) => ids.has(p.id));
  }

  return filteredRows;
};

export const buildFiltroFromQuickFilter = (quickFilter) => {
  const filtroValues = {};

  if (quickFilter.search && quickFilter.search.trim()) {
    const term = quickFilter.search.trim();
    if (/^\d+$/.test(term)) {
      filtroValues.numeroPoste = term;
    } else {
      filtroValues.endereco = term;
    }
  }

  if (quickFilter.tipoChip) {
    filtroValues.tipoChip = quickFilter.tipoChip;
  }

  if (quickFilter.statusChip) {
    filtroValues.statusDigitacao = [quickFilter.statusChip];
  }

  if (quickFilter.situacaoChip) {
    filtroValues.situacaoPostes = [quickFilter.situacaoChip];
  }

  return filtroValues;
};

export const isUsoMutuoFilterActive = (filtroValues) =>
  Boolean(filtroValues && Object.keys(filtroValues).length > 0);

export const isUsoMutuoQuickFilterActive = (quickFilter) =>
  isUsoMutuoFilterActive(buildFiltroFromQuickFilter(quickFilter));
